import React from 'react';
import JSON5 from 'json5';
import CodeBlock from '../code/CodeBlock';
import Chart from './Chart';
import DataTable from './DataTable';
import Calculator from './Calculator';
import Cards from './Cards';
import { Timeline } from './Sequence';
import { Metrics, Tabs, Suggestions } from './Layout';
import { Skeleton } from './shared';

/**
 * Bloco ```ui da resposta: um JSON que vira componente (gráfico, tabela,
 * métricas, cartões, linha do tempo, abas, calculadora, sugestões —
 * formatos na skill "componentes" da API). O modelo escreve; o chat desenha com o
 * próprio design — nada de HTML/JS gerado. JSON ainda incompleto (o bloco está
 * chegando no stream) mostra um esqueleto; JSON quebrado ou tipo desconhecido
 * volta a ser um bloco de código, sem derrubar a mensagem.
 */

export const GENUI_LANG = 'ui';

/** Chaves/colchetes ainda abertos (fora de strings): o JSON não terminou. */
export function isIncomplete(src: string): boolean {
  let depth = 0;
  let inStr: string | null = null;
  let esc = false;
  for (const ch of src) {
    if (inStr) {
      if (esc) esc = false;
      else if (ch === '\\') esc = true;
      else if (ch === inStr) inStr = null;
      continue;
    }
    if (ch === '"' || ch === "'") inStr = ch;
    else if (ch === '{' || ch === '[') depth += 1;
    else if (ch === '}' || ch === ']') depth -= 1;
  }
  return depth > 0 || inStr !== null || !src.trim();
}

export type ParsedBlock =
  | { state: 'loading' }
  | { state: 'invalid' }
  | { state: 'ok'; spec: any };

// Vícios de quem copia a notação de um esquema em vez do exemplo: "campo?"
// como chave e "a"|"b" como valor. Corrige em vez de jogar o bloco fora.
const ALTERNATIVES = /"((?:[^"\\]|\\.)*)"(?:\s*\|\s*"(?:[^"\\]|\\.)*")+/g;

function normalizeKeys(value: any): any {
  if (Array.isArray(value)) return value.map(normalizeKeys);
  if (value && typeof value === 'object') {
    const out: Record<string, any> = {};
    for (const [k, v] of Object.entries(value)) {
      const key = k.replace(/\?+$/, '');
      if (!(key in out) || k === key) out[key] = normalizeKeys(v);
    }
    return out;
  }
  return value;
}

export function parseBlock(src: string): ParsedBlock {
  try {
    let raw: any;
    try {
      raw = JSON5.parse(src);
    } catch (err) {
      // Só conserta "a"|"b" quando o JSON não fecha: um "|" dentro de texto
      // (tabela em markdown no "conteudo") nunca é tocado.
      if (isIncomplete(src)) throw err;
      raw = JSON5.parse(src.replace(ALTERNATIVES, '"$1"'));
    }
    const spec = normalizeKeys(raw);
    if (spec && typeof spec === 'object' && !Array.isArray(spec) && RENDERERS[spec.tipo]) {
      return { state: 'ok', spec };
    }
    return { state: 'invalid' };
  } catch {
    return isIncomplete(src) ? { state: 'loading' } : { state: 'invalid' };
  }
}

const RENDERERS: Record<string, React.ComponentType<{ spec: any }>> = {
  grafico: Chart,
  tabela: DataTable,
  metricas: Metrics,
  cartoes: Cards,
  linha_do_tempo: Timeline,
  abas: Tabs,
  calculadora: Calculator,
  sugestoes: Suggestions,
};

class BlockBoundary extends React.Component<{ fallback: React.ReactNode; children: React.ReactNode }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch() { /* um componente com dados ruins vira código, sem derrubar a mensagem */ }
  render() { return this.state.failed ? this.props.fallback : this.props.children; }
}

const textOf = (children: React.ReactNode): string => React.Children.toArray(children)
  .map((c) => (typeof c === 'string' || typeof c === 'number' ? String(c) : ''))
  .join('');

function GenerativeBlock({ codeChildren }: { codeChildren: React.ReactNode }) {
  const src = textOf(codeChildren);
  const parsed = parseBlock(src);
  const raw = <CodeBlock lang="json" codeChildren={codeChildren as any} />;
  if (parsed.state === 'loading') return <Skeleton data-testid="genui-loading">Montando visualização…</Skeleton>;
  if (parsed.state === 'invalid') return raw;
  const Renderer = RENDERERS[parsed.spec.tipo];
  return (
    <BlockBoundary fallback={raw}>
      <Renderer spec={parsed.spec} />
    </BlockBoundary>
  );
}

export default React.memo(GenerativeBlock);
